// Flight calculations: leg distance/course/heading, ETE, fuel.
//
// v1: no-wind. Math is wired so wind can plug in later via WindAloft on a
// per-leg basis without touching consumers.
//
// All angles in DEGREES. Distances in NAUTICAL MILES. Speeds in KNOTS.
// Fuel in GALLONS (gph).
//
// Cross-checked vs OpenAI Codex 2026-05-08 (geo math); wind triangle math
// here also re-implementable in seconds for cross-check.

import { greatCircleNm, bearingDeg } from './geo';
import type { PlanPoint } from '../stores/planStore';
import type { AircraftProfile } from '../stores/aircraftStore';
import { activeCruise } from '../stores/aircraftStore';

export interface WindAloft {
  /** Wind FROM direction (true degrees), e.g. 270 = wind from west */
  dirFromTrue: number;
  /** Speed in knots */
  speedKt: number;
}

export interface LegCalc {
  from: PlanPoint;
  to: PlanPoint;
  distanceNm: number;
  trueCourseDeg: number;
  /** Wind-corrected magnetic heading; null if mag var or wind unknown. */
  magneticHeadingDeg: number | null;
  /** Cruise TAS used (knots) — from aircraft profile if present. */
  tasKt: number | null;
  /** Ground speed (knots) — wind-corrected; falls back to TAS if no wind. */
  groundspeedKt: number | null;
  /** ETE in minutes; null if no TAS. */
  eteMinutes: number | null;
  /** Fuel burn for this leg in gallons; null if no burn rate. */
  fuelGal: number | null;
  /** True altitude target — same as cruiseAlt unless leg is climb/descent. */
  altitudeFt: number | null;
}

export interface FlightCalcInput {
  route: PlanPoint[];
  aircraft: AircraftProfile | null;
  cruiseAlt: number | null;
  /** Optional per-leg winds; aligned to legs[]. Missing entries = no wind. */
  winds?: (WindAloft | null)[];
  /** Optional global mag variation in degrees east-positive applied to TC->MH.
   *  Typical for CONUS: 0 (east coast) to +20 (Pacific NW). Default 0. */
  magVarDeg?: number;
  /** Optional explicit performance profile id (e.g. plan store's
   *  performanceProfileId). When omitted, the default-flagged profile
   *  or the first profile on the aircraft is used. */
  performanceProfileId?: string | null;
}

export interface FlightCalcResult {
  legs: LegCalc[];
  totalNm: number;
  totalEteMinutes: number | null;
  totalFuelGal: number | null;
  /** Rough endurance from on-board fuel & burn rate (hours). */
  enduranceHours: number | null;
}

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/**
 * Solve the wind triangle for ground speed + heading required to track a
 * desired course at a given TAS through a wind FROM `dirFromTrue` at speed
 * `speedKt`.
 *
 * Returns NaN values if a no-wind solution doesn't exist (TAS < |wind|
 * components on certain courses); caller should fall back.
 */
export function windTriangle(
  trueCourseDeg: number,
  tasKt: number,
  wind: WindAloft | null
): { groundspeedKt: number; trueHeadingDeg: number } {
  if (!wind || wind.speedKt <= 0) {
    return { groundspeedKt: tasKt, trueHeadingDeg: trueCourseDeg };
  }
  // Convention: wind reported FROM dirFromTrue at speedKt.
  // Wind angle relative to course. Use the FROM direction directly so a
  // positive xw means "wind from the right" (pushing aircraft left).
  //
  // Step 1: compute the angle FROM-direction is from the course.
  //   delta > 0 ⇒ wind from the right of course
  //   delta < 0 ⇒ wind from the left of course
  const delta = ((wind.dirFromTrue - trueCourseDeg + 540) % 360) - 180; // -180..180
  // Crosswind component (positive when wind is from the right):
  const xw = wind.speedKt * Math.sin(toRad(delta));
  // Headwind component (positive when wind has a component on the nose):
  const hw = wind.speedKt * Math.cos(toRad(delta));

  // Wind correction angle: aircraft must crab INTO the wind.
  // If wind is from the right (xw > 0), heading is to the right of course
  // (corrDeg > 0). asin(xw/TAS) gives the correct sign automatically.
  const sinCorr = xw / tasKt;
  if (Math.abs(sinCorr) > 1) {
    return { groundspeedKt: NaN, trueHeadingDeg: NaN };
  }
  const corrDeg = toDeg(Math.asin(sinCorr));
  const trueHeading = (trueCourseDeg + corrDeg + 360) % 360;
  // Ground speed = TAS along course minus headwind:
  const gs = tasKt * Math.cos(toRad(corrDeg)) - hw;
  return { groundspeedKt: gs, trueHeadingDeg: trueHeading };
}

/** Convert true to magnetic. magVar east-positive (US common: +5 east coast, +15 NorCal). */
export function trueToMagnetic(trueDeg: number, magVarDeg: number): number {
  return ((trueDeg - magVarDeg) % 360 + 360) % 360;
}

export function calcFlight(input: FlightCalcInput): FlightCalcResult {
  const { route, aircraft, cruiseAlt, winds, magVarDeg = 0 } = input;
  const { cruiseKt, fuelBurnGph } = activeCruise(aircraft, input.performanceProfileId ?? null);
  const tas = cruiseKt ?? null;
  const burn = fuelBurnGph ?? null;
  const fuelOnboard = aircraft?.fuel?.totalUsable ?? null;

  const legs: LegCalc[] = [];
  let totalNm = 0;
  let totalEteMin: number | null = 0;
  let totalFuelGal: number | null = 0;

  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1];
    const b = route[i];
    const distance = greatCircleNm(a.lat, a.lon, b.lat, b.lon);
    const tc = bearingDeg(a.lat, a.lon, b.lat, b.lon);
    const wind = (winds && winds[i - 1]) || null;

    let gs: number | null = null;
    let mh: number | null = null;
    if (tas != null) {
      const wt = windTriangle(tc, tas, wind);
      if (Number.isFinite(wt.groundspeedKt) && Number.isFinite(wt.trueHeadingDeg)) {
        gs = wt.groundspeedKt;
        mh = trueToMagnetic(wt.trueHeadingDeg, magVarDeg);
      } else {
        // Fallback if wind triangle has no solution
        gs = tas;
        mh = trueToMagnetic(tc, magVarDeg);
      }
    }

    const eteMin = (gs != null && gs > 0) ? (distance / gs) * 60 : null;
    const fuelGal = (eteMin != null && burn != null) ? (eteMin / 60) * burn : null;

    legs.push({
      from: a, to: b,
      distanceNm: distance,
      trueCourseDeg: tc,
      magneticHeadingDeg: mh,
      tasKt: tas,
      groundspeedKt: gs,
      eteMinutes: eteMin,
      fuelGal,
      altitudeFt: cruiseAlt,
    });
    totalNm += distance;
    if (eteMin == null) totalEteMin = null;
    else if (totalEteMin != null) totalEteMin += eteMin;
    if (fuelGal == null) totalFuelGal = null;
    else if (totalFuelGal != null) totalFuelGal += fuelGal;
  }

  const enduranceHours =
    (fuelOnboard != null && burn != null && burn > 0) ? fuelOnboard / burn : null;

  return { legs, totalNm, totalEteMinutes: totalEteMin, totalFuelGal, enduranceHours };
}

export function formatHm(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  if (h === 0) return `${m}m`;
  return `${h}h${m.toString().padStart(2, '0')}m`;
}

export function addMinutesIso(iso: string, minutes: number): string {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + minutes);
  return d.toISOString();
}
